import { FC, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trpc } from 'src/trpc';
import { useToast } from 'components/useToast';
import { addressRestrictions } from '@libs/schemas';
import { timeRanges } from 'src/utils/time-ranges';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form } from '@/components/ui/form';
import { Loader } from 'src/components/Loader';
import { MapPin, Calendar, Shield, Info } from 'lucide-react';
import { AddressAndScheduleTab } from './AddressAndScheduleTab';
import { DeliveryDetailsTab } from './DeliveryDetailsTab';
import { AddressZ } from 'src/components/address/AddressForm';
import { toNoonUTC, toValidDate } from '@libs/frontend-services';
import { MatchableAddress, isAddressChanged } from '../address-matching';

const orderDeliveryFormSchema = z
  .object({
    restrictions: z.record(z.string(), z.string()),
    placementInstructions: z.string().optional(),
    dropOffWindow: z.object({
      day: z.date(),
      range: z.string(),
    }),
    pickupWindow: z.object({
      day: z.date(),
      range: z.string(),
    }),
  })
  .refine((data) => new Date(data.dropOffWindow.day) < new Date(data.pickupWindow.day), {
    message: 'Pickup date cannot be earlier than dropoff date.',
    path: ['pickupWindow.day'],
  });

type OrderDeliveryFormData = z.infer<typeof orderDeliveryFormSchema>;

/**
 * The restriction keys the Delivery Details tab owns, in the order it renders them.
 *
 * Deliberately not `addressRestrictions` from `@libs/schemas` - that is the customer-facing
 * checklist ('No street parking', 'Steep driveway', ...) and shares no key with these, so
 * comparing against it would report every save as unchanged.
 */
const sheetRestrictionKeys = [
  'Gated Community',
  'HOA Gate Hours',
  'Required Credentials',
  'Lock Code/Hours',
  'Other Notes',
] as const;

interface OrderDeliveryEditSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'address-schedule' | 'delivery-details';
}

export const OrderDeliveryEditSheet: FC<OrderDeliveryEditSheetProps> = ({
  isOpen,
  onClose,
  initialTab = 'address-schedule',
}) => {
  const params = useParams();
  const utils = trpc.useUtils();
  const { successToast, errorToast } = useToast();
  const [activeTab, setActiveTab] = useState(initialTab);

  const { data: order, isLoading: orderIsLoading } =
    trpc.admin.order.adminGetOrderRelatedInfoById.useQuery(params.id!);

  // Address-related state
  const [addressFormData, setAddressFormData] = useState<
    { address: AddressZ; placeId: string } | undefined
  >(undefined);

  const proposedAddress = useMemo<MatchableAddress | null>(() => {
    if (!addressFormData?.address?.line1) {
      return null;
    }
    return {
      line1: addressFormData.address.line1,
      line2: addressFormData.address.line2,
      city: addressFormData.address.city,
      state: addressFormData.address.state,
      postalCode: addressFormData.address.postalCode,
      googlePlacesId: addressFormData.placeId || undefined,
    };
  }, [addressFormData]);

  // Restriction-related state
  const [isGatedCommunity, setIsGatedCommunity] = useState(false);
  const [gateCode, setGateCode] = useState('');
  const [hoaGateHours, setHoaGateHours] = useState('');
  const [requiredCredentials, setRequiredCredentials] = useState('');
  const [lockCodeAndHours, setLockCodeAndHours] = useState('');
  const [otherNotes, setOtherNotes] = useState('');

  // Memoize initialRestrictions so it updates when order data loads
  const initialRestrictions = useMemo(() => {
    return addressRestrictions.reduce((accum, cur) => {
      return {
        ...accum,
        [cur]: (order?.address?.restrictions || {})[cur] || '',
      };
    }, {}) as {
      [_K in (typeof addressRestrictions)[number]]: string;
    };
  }, [order?.address?.restrictions]);

  const form = useForm<OrderDeliveryFormData>({
    resolver: zodResolver(orderDeliveryFormSchema),
    values: {
      dropOffWindow: {
        // Invalid Date compares unequal to itself in react-hook-form's values diff,
        // causing a reset loop even while this sheet is closed.
        day: toValidDate(order?.order?.dropOffWindow?.day) ?? new Date(new Date().toDateString()),
        range: '6:00 AM - 6:00 PM',
      },
      pickupWindow: {
        day: toValidDate(order?.order?.pickupWindow?.day) ?? new Date(new Date().toDateString()),
        range: '6:00 AM - 6:00 PM',
      },
      restrictions: initialRestrictions,
      placementInstructions: order?.order?.placementInstructions || '',
    },
  });

  // Populate restrictions from existing order data
  useEffect(() => {
    if (order?.address?.restrictions) {
      const restrictions = order.address.restrictions;

      if (restrictions['Gated Community']) {
        setIsGatedCommunity(true);
        setGateCode(restrictions['Gated Community']);
      }
      if (restrictions['HOA Gate Hours']) {
        setHoaGateHours(restrictions['HOA Gate Hours']);
      }
      if (restrictions['Required Credentials']) {
        setRequiredCredentials(restrictions['Required Credentials']);
      }
      if (restrictions['Lock Code/Hours']) {
        setLockCodeAndHours(restrictions['Lock Code/Hours']);
      }
      if (restrictions['Other Notes']) {
        setOtherNotes(restrictions['Other Notes']);
      }
    }
  }, [order?.address?.restrictions]);

  // Reset tab when sheet opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // The customer's saved addresses, used to tell the admin whether saving will reuse one of
  // them or store a new record.
  const { data: savedAddresses } = trpc.admin.address.adminListUserAddresses.useQuery(
    order?.customer?.userId as string,
    { enabled: !!order?.customer?.userId },
  );

  // Address changes go through the order correction mutation, which reuses a matching saved
  // address, clones instead of editing in place when the address differs (so orders sharing a
  // record are never silently rewritten), syncs the linked cart and writes an audit note.
  const { mutateAsync: correctAddressMutation, isPending: isAddressUpserting } =
    trpc.admin.order.adminCorrectOrderDeliveryAddress.useMutation();

  // Mutation for updating order
  const { mutate, isPending: isOrderUpdating } =
    trpc.admin.order.adminUpdateDropoffDetails.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(params.id!);
        successToast({ title: 'Order delivery details updated successfully' });
        onClose();
      },
      onError: (error) => {
        errorToast({ title: 'Error updating delivery details', description: error.message });
      },
    });

  const isPending = isAddressUpserting || isOrderUpdating;

  // A correction on a paid or closed-out order is allowed on purpose - the mistake is usually
  // only caught after payment - but the admin should know it lands on a settled order. This is a
  // non-blocking heads-up, not a guard: nothing here is disabled, no re-charge or email is
  // triggered by editing the address/schedule.
  const isSettledOrder =
    (order?.order?.payments?.length ?? 0) > 0 || order?.order?.status === 'completed';

  const handleSubmit = async (data: OrderDeliveryFormData) => {
    const timeRange = timeRanges.find((time) => time.label === '6:00 AM - 6:00 PM')!;

    // Every key this sheet owns, including the ones the admin left or made blank. The backend
    // merges the map key by key, so a blank is the only way to say "clear this one" - omitting
    // it would read as "no opinion" and leave the old value in place.
    const restrictions: Record<string, string> = {
      'Gated Community': isGatedCommunity ? gateCode : '',
      'HOA Gate Hours': hoaGateHours,
      'Required Credentials': requiredCredentials,
      'Lock Code/Hours': lockCodeAndHours,
      'Other Notes': otherNotes,
    };

    // Value-level dirty check against the address this sheet loaded, the same shape of test
    // `isAddressChanged` does for the address. Nothing edited in the Delivery Details tab means
    // there is nothing to write, so the save leaves the address record's restrictions alone.
    //
    // Only the keys the admin actually changed are sent - not the whole assembled map. The
    // untouched keys still hold the values this sheet loaded from the address it opened on; if the
    // same save also corrects the address to one of the customer's already-saved records (which
    // the backend reuses in place rather than cloning), re-posting those untouched keys would
    // stamp this order's old values onto a record other orders share - overwriting a gate code
    // from a different site, or blanking a value the sheet never displayed. Diffing keeps the
    // admin authoritative for exactly the keys they touched, on whichever record the order lands.
    const loadedRestrictions = order?.address?.restrictions || {};
    const changedRestrictions: Record<string, string> = Object.fromEntries(
      sheetRestrictionKeys
        .filter((key) => (restrictions[key] || '') !== (loadedRestrictions[key] || ''))
        .map((key) => [key, restrictions[key] || '']),
    );
    const restrictionsChanged = Object.keys(changedRestrictions).length > 0;

    let finalAddressId = order?.order?.deliveryAddressId;

    // Only run the correction when the address actually changed - the address form fires a
    // change event on mount, so the presence of form state alone is not a real edit.
    if (proposedAddress && isAddressChanged(proposedAddress, order?.address)) {
      try {
        const result = await correctAddressMutation({
          orderId: order!.order!._id!,
          line2: addressFormData!.address.line2?.trim() || undefined,
          ...(addressFormData!.placeId
            ? { placeId: addressFormData!.placeId }
            : {
                manualAddress: {
                  line1: addressFormData!.address.line1.trim(),
                  city: addressFormData!.address.city.trim(),
                  state: addressFormData!.address.state.trim().toUpperCase(),
                  postalCode: addressFormData!.address.postalCode.trim(),
                  country: addressFormData!.address.country || 'US',
                },
              }),
        });
        finalAddressId = result.addressId;
        utils.admin.address.adminListUserAddresses.invalidate(order!.customer!.userId!);
        utils.admin.note.getNotes.invalidate();
      } catch (error) {
        errorToast({
          title: 'Error saving address',
          description: error instanceof Error ? error.message : 'Failed to save address',
        });
        return;
      }
    }

    if (!finalAddressId) {
      errorToast({
        title: 'No delivery address',
        description: 'Please add a delivery address',
      });
      return;
    }

    // Now update the order with all details
    const orderPayload = {
      orderId: order!.order!._id!,
      deliveryAddressId: finalAddressId,
      dropOffWindow: {
        start: timeRange.start,
        end: timeRange.end,
        day: new Date(toNoonUTC(data.dropOffWindow.day)).toISOString(),
      },
      pickupWindow: {
        start: timeRange.start,
        end: timeRange.end,
        day: new Date(toNoonUTC(data.pickupWindow.day)).toISOString(),
      },
      // Send restrictions only when the admin actually edited them in this submission.
      //
      // Omitting them is what keeps a correction's carried-over copy intact: the sheet loaded
      // its values from the *previous* address, so re-posting an untouched map would write that
      // stale state back over whatever the correction just put on the new record.
      //
      // But an edit the admin did make has to land, and it has to land on `finalAddressId` -
      // the record the order ends up pointing at, which after a correction is the new one, not
      // the address this sheet loaded. The backend merges the sent keys over what is already on
      // that record rather than replacing the map wholesale, so restrictions the sheet does not
      // know about - and the untouched keys omitted here - survive the write either way.
      ...(restrictionsChanged ? { restrictions: changedRestrictions } : {}),
      placementInstructions: data.placementInstructions,
    };
    mutate(orderPayload);
  };

  // Handler for address form changes
  const handleAddressFormChange = (address: AddressZ, placeId: string) => {
    setAddressFormData({ address, placeId });
  };

  if (orderIsLoading) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          side="right"
          className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
        >
          <div className="flex items-center justify-center h-full">
            <Loader title="Loading order details" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
      >
        <div className="grid h-full grid-rows-[auto_auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <MapPin className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  Edit Delivery Details
                </SheetTitle>
                <p className="text-sm text-gray-500">Order #{order?.order?.friendlyId}</p>
              </div>
            </div>
          </SheetHeader>

          {/* Navigation Tabs */}
          <div className="border-b bg-gray-100 px-6 py-3">
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as 'address-schedule' | 'delivery-details')
              }
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 bg-gray-200 p-1 rounded-lg">
                <TabsTrigger
                  value="address-schedule"
                  className="flex items-center justify-center space-x-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Address & Schedule</span>
                </TabsTrigger>
                <TabsTrigger
                  value="delivery-details"
                  className="flex items-center justify-center space-x-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
                >
                  <Shield className="h-4 w-4" />
                  <span>Delivery Details</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Content Area - Scrollable */}
          <div className="overflow-y-auto">
            {isSettledOrder && (
              <div
                data-testid="settled-order-notice"
                className="mx-6 mt-6 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
              >
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  This order has already been paid or closed out. You can still correct it — the
                  customer won&apos;t be re-charged and no new confirmation emails are sent.
                </span>
              </div>
            )}
            <Form {...form}>
              <form
                id="order-delivery-form"
                onSubmit={form.handleSubmit(handleSubmit)}
                onKeyDown={(e) => {
                  e.key === 'Enter' && e.preventDefault();
                }}
              >
                <Tabs
                  value={activeTab}
                  onValueChange={(value) =>
                    setActiveTab(value as 'address-schedule' | 'delivery-details')
                  }
                  className="w-full"
                >
                  <TabsContent value="address-schedule" className="mt-0 p-6">
                    <AddressAndScheduleTab
                      control={form.control}
                      userId={order?.customer?.userId}
                      address={order?.address ?? undefined}
                      onAddressFormChange={handleAddressFormChange}
                      pendingAddress={addressFormData}
                      savedAddresses={savedAddresses}
                    />
                  </TabsContent>

                  <TabsContent value="delivery-details" className="mt-0 p-6">
                    <DeliveryDetailsTab
                      control={form.control}
                      isGatedCommunity={isGatedCommunity}
                      onGatedCommunityChange={setIsGatedCommunity}
                      gateCode={gateCode}
                      onGateCodeChange={setGateCode}
                      hoaGateHours={hoaGateHours}
                      onHoaGateHoursChange={setHoaGateHours}
                      requiredCredentials={requiredCredentials}
                      onRequiredCredentialsChange={setRequiredCredentials}
                      lockCodeAndHours={lockCodeAndHours}
                      onLockCodeAndHoursChange={setLockCodeAndHours}
                      otherNotes={otherNotes}
                      onOtherNotesChange={setOtherNotes}
                    />
                  </TabsContent>
                </Tabs>
              </form>
            </Form>
          </div>

          {/* Footer */}
          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="order-delivery-form"
                disabled={isPending}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
